import type { ParkingSpotType } from './Enums';

/**
 * Parkeringskö at `parkingQueue/{entryKey}`: a flat root indexed on
 * `tenantKey`, readable and writable by admins only (unlike leases, which every
 * listed user may read). One entry is one applicant's place in the queue for
 * ONE spot: a tenant who wants two spots has two entries. The entry is
 * instance-wide; its wish is a set of parking areas in any buildings, so "the
 * queue for building B" is derived (entries wanting an area in B) and never
 * stored. The append-only log nests under the entry. Everything a row shows
 * beyond these fields (spots held, time waited, last touched) is derived in
 * `Helpers/parkingQueueUtils.ts`, never stored. A spot goes to an entry as a
 * hyresavtal written from the queue, or matched to the entry after one written
 * elsewhere: the entry closes as `accepted` and logs `leaseLinked`.
 */

export type QueueEntryStatus = 'active' | 'closed';

export type QueueCloseReason = 'accepted' | 'withdrawn' | 'other';

/** Three-state wish: absent = any. */
export type QueueWish = 'yes' | 'no';

export type QueueLogEventType =
    | 'created'
    | 'queueDateChanged'
    | 'internalChanged'
    | 'areasChanged'
    | 'spotTypesChanged'
    | 'chargerChanged'
    | 'outletChanged'
    | 'commentChanged'
    | 'closed'
    | 'reopened'
    | 'deviation'
    | 'leaseLinked'
    | 'note';

export interface QueueLogEventData {
    type: QueueLogEventType;
    actor: string;          // UID, resolved to a name at display time
    when: string;           // Stockholm 'YYYY-MM-DD hh:mm:ss'
    note?: string;
    /**
     * Old / new value for field changes, close reason for `closed`, spot label
     * → lease key for `leaseLinked`, spot label → ahead count for `deviation`.
     */
    from?: string;
    to?: string;
    /** `deviation` only: the entries passed over (keys, resolved to names at display time). */
    aheadEntryKeys?: { [entryKey: string]: true };
}

export interface QueueLogEvent extends QueueLogEventData {
    key: string;
}

export interface ParkingQueueEntryData {
    tenantKey: string;
    /** YYYY-MM-DD; may be earlier than `createdWhen` (backdated from an older list). */
    queueDate: string;
    status: QueueEntryStatus;
    closeReason?: QueueCloseReason;
    closeNote?: string;
    /** Area key → the building it lies in, at least one; the value lets readers resolve the area without scanning buildings. */
    wantedAreas: { [areaKey: string]: string };
    /** Absent or empty = any type. */
    spotTypes?: { [type in ParkingSpotType]?: true };
    wantsCharger?: QueueWish;
    wantsOutlet?: QueueWish;
    comment?: string;
    /**
     * "Räknas som intern", set by hand for applicants who live or have premises
     * in the instance without a lease to show it (e.g. BRF members). Intern =
     * an active or upcoming apartment/lokal lease involving the tenant (derived,
     * never stored) OR this flag. Absent = false.
     */
    markedInternal?: true;
    log?: { [eventKey: string]: QueueLogEventData };
    createdBy: string;
    createdWhen: string;
    updatedBy?: string;
    updatedWhen?: string;
}

export interface ParkingQueueEntry extends Omit<ParkingQueueEntryData, 'log'> {
    key: string;
    /** Oldest first. */
    log: QueueLogEvent[];
}

/** Editable entry fields (create and edit). */
export interface ParkingQueueEntryInput {
    tenantKey: string;
    queueDate: string;
    wantedAreas: { [areaKey: string]: string };
    spotTypes?: ParkingSpotType[];
    wantsCharger?: QueueWish;
    wantsOutlet?: QueueWish;
    comment?: string;
    markedInternal?: boolean;
}

/** The spot a hyresavtal for the entry rents. */
export interface QueueSpotTarget {
    buildingKey: string;
    areaKey: string;
    spotKey: string;
}

/** Links a written lease to the entry and closes it. */
export interface QueueLeaseLinkInput extends QueueSpotTarget {
    leaseKey: string;
    /** Required when the deviation check finds entries ahead; ignored otherwise. */
    reason?: string;
}

export const QUEUE_CLOSE_REASON_LABELS: Record<QueueCloseReason, string> = {
    accepted: 'Fick hyresavtal',
    withdrawn: 'Återtagen',
    other: 'Annat',
};

export const QUEUE_WISH_LABELS: Record<QueueWish | 'any', string> = {
    yes: 'Ja',
    no: 'Nej',
    any: 'Spelar ingen roll',
};

/** The value written into every note when a tenant is anonymised (mf-functions `anonymizeTenant`). */
export const ANONYMIZED_NOTE = '(borttagen)';
